const STORAGE_KEY = "jourfx_versicherten_profile_session";

export interface VersichertenSession {
  versichertenProfileId: string;
  sessionToken: string;
  expiresAt: number;
  validatedAt: number;
}

function normalizeProfileId(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toUnixMs(value: unknown): number | null {
  const normalizeEpoch = (parsed: number): number => {
    if (parsed > 0 && parsed < 1_000_000_000_000) {
      return parsed * 1000;
    }
    return parsed;
  };

  if (typeof value === "number" && Number.isFinite(value)) {
    return normalizeEpoch(value);
  }
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return normalizeEpoch(parsed);

    const parsedDate = Date.parse(value);
    return Number.isFinite(parsedDate) ? parsedDate : null;
  }
  return null;
}

export function getVersichertenSession(): VersichertenSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const versichertenProfileId = normalizeProfileId(parsed.versichertenProfileId);
    const expiresAt = toUnixMs(parsed.expiresAt);
    const validatedAt = toUnixMs(parsed.validatedAt);

    if (
      versichertenProfileId === null ||
      typeof parsed.sessionToken !== "string" ||
      expiresAt === null ||
      validatedAt === null
    ) {
      clearVersichertenSession();
      return null;
    }

    const session: VersichertenSession = {
      ...parsed,
      versichertenProfileId,
      expiresAt,
      validatedAt,
    };

    if (session.expiresAt < Date.now()) {
      clearVersichertenSession();
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

export function setVersichertenSession(session: VersichertenSession): void {
  const versichertenProfileId = normalizeProfileId(session.versichertenProfileId);
  if (versichertenProfileId === null) {
    clearVersichertenSession();
    return;
  }

  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      ...session,
      versichertenProfileId,
    })
  );
}

export function clearVersichertenSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function isVersichertenSessionValid(versichertenProfileId: string): boolean {
  const session = getVersichertenSession();
  const targetId = normalizeProfileId(versichertenProfileId);
  return (
    session !== null &&
    targetId !== null &&
    session.versichertenProfileId === targetId &&
    session.expiresAt >= Date.now()
  );
}
